import { useState, useRef, useEffect, KeyboardEvent } from "react";
import { View, TextInput, Pressable } from "react-native-web";
import { useLocation, useNavigate } from "react-router-dom";
import CustomText from "../components/atoms/CustomText";
import Button from "../components/common/Button";
import { colors, spacing } from "../utils/styling";
import { getResultsDataFromStateOrStorage, ResultsData } from "../utils/resultsData";
import { useSignUpWithPassword } from "../lib/auth";
import { useUpdateQuizWithProfile } from "../lib/quizzes";
import { useAttachExercisesToProfile } from "../lib/exercises";
import { useUpdateSubscriptionWithProfile } from "../lib/subscriptions";
import { getTransactionId } from "../lib/stripe";
import { useResponsive } from "../hooks/useResponsive";
import SvgEye from "../components/icons/SvgEye";
import { useUpdateCurrentProfile } from "../lib/profiles";

// ResultsData type imported from utils/resultsData

const handleApiError = (error: any) => {
  if (error.includes("400")) {
    return "Please ensure email is valid and password is at least 6 characters long."
  }
  return (
    <div>
      {error}
    </div>
  );
}

export default function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const mobileLogoSize = 108;

  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  // Set when the email already has an account (e.g. someone retaking the quiz).
  // Before this, that case showed the generic "ensure email is valid" message and
  // the person had no way forward — Nina hit this on the 9/23 call.
  const [accountExists, setAccountExists] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [isSigningUp, setIsSigningUp] = useState(false);

  const { mutateAsync: signUp } = useSignUpWithPassword();
  const { mutateAsync: updateQuizWithProfile } = useUpdateQuizWithProfile();
  const { mutateAsync: attachExercisesToProfile } = useAttachExercisesToProfile();
  const { mutateAsync: updateSubscriptionWithProfile } =
    useUpdateSubscriptionWithProfile();
  const { mutateAsync: updateCurrentProfile } = useUpdateCurrentProfile();

  // Unified results data loader
  let resultsData = getResultsDataFromStateOrStorage(location.state);

  const defaultResultsData: ResultsData = {
    answers: [],
    specificAnswers: [],
    categoryScores: {},
    specificCategoryScores: {},
    dominantCategory: "",
    specificDominantCategory: "",
    sfAccount: undefined,
    quizId: undefined,
    leadId: undefined,
    email: undefined,
    firstName: "",
    lastName: "",
    phone: "",
    paymentConfirmed: false,
  };

  // Use resultsData if available, otherwise use defaults
  const safeResultsData = resultsData || defaultResultsData;

  const { email, paymentConfirmed, firstName, lastName, phone, sfAccount } =
    safeResultsData;

  const handleSignUp = async () => {
    // Clear any previous errors
    setError("");

    // Validate inputs
    if (!email || !password || !firstName || !lastName) {
      setError("Please enter both email and password");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSigningUp(true);

    try {
      // Attempt to sign up - returns session with profile data
      const session = await signUp({
        email,
        password,
        is_client: true, // is_client = true, not an advisor
        first_name: firstName,
        last_name: lastName,
        phone: phone || undefined,
        sf_account: sfAccount || undefined,
      });

      // If we have a quizId, link it to the new profile
      if (safeResultsData.quizId && session?.session?.user?.id) {
        try {
          await updateQuizWithProfile({
            quizId: safeResultsData.quizId,
            profile: session.session.user.id,
          });
          console.log("✅ Quiz linked to new profile successfully");
        } catch (quizError) {
          console.error("⚠️ Failed to link quiz to profile:", quizError);
          // Don't fail the signup if quiz linking fails
        }

        // Also link any workbook exercise answers entered before signup to the
        // new profile, so the user can return to them after re-login (86badfk6e).
        try {
          await attachExercisesToProfile({
            quizId: safeResultsData.quizId,
            profileId: session.session.user.id,
          });
          console.log("✅ Exercise responses linked to new profile");
        } catch (exErr) {
          console.error("⚠️ Failed to link exercise responses to profile:", exErr);
          // Non-fatal — exercises remain accessible by quiz_id regardless.
        }
      }

      // If we have a quizId, also try to link any associated subscription to the profile
      if (safeResultsData.quizId && session?.session?.user?.id) {
        try {
          console.log(
            "🔍 Looking for subscription associated with quiz:",
            safeResultsData.quizId
          );
          const transactionInfo = await getTransactionId(
            safeResultsData.quizId
          );

          if (
            transactionInfo.status === "found" &&
            transactionInfo.type === "subscription" &&
            transactionInfo.subscription_id
          ) {
            console.log(
              "🔗 Found subscription, linking to profile:",
              transactionInfo.subscription_id
            );
            await updateSubscriptionWithProfile({
              subscriptionId: transactionInfo.subscription_id,
              profile: session.session.user.id,
            });
            console.log("✅ Subscription linked to new profile successfully");
          } else {
            console.log(
              "ℹ️ No subscription found for quiz or not a subscription type:",
              transactionInfo
            );
          }
        } catch (subscriptionError) {
          console.error(
            "⚠️ Failed to link subscription to profile:",
            subscriptionError
          );
          // Don't fail the signup if subscription linking fails
        }
      }

      const rawProfileAnswers = localStorage.getItem("mpay_personalInfo")
      if (rawProfileAnswers) {
        try {
          const profileInfo = JSON.parse(rawProfileAnswers) as Record<string, string>;
          await updateCurrentProfile({
            info: profileInfo,
          });
        } catch (e) {
          console.error("Issue connecting personal info to User Profile");
        }
      }
      
      // We know this user is a client, so route to client portal
      navigate("/client", {
        state: {
          ...safeResultsData,
          paymentConfirmed: true,
          from: location.pathname,
        },
      });
    } catch (err) {
      // Handle signup error
      const detail = (err as any)?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("already registered")) {
        setAccountExists(true);
        setError("");
        setIsSigningUp(false);
        return;
      }
      setError(
        err instanceof Error ? err.message : "Signup failed. Please try again."
      );
      setIsSigningUp(false);
    }
  };

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  return (
    <View
      style={{
        flex: 1,
        flexDirection: isMobile ? "column" : "row",
        marginTop: isMobile ? 12.5 : undefined,
      }}
    >
      {/* Left Side - Login Form */}
      <View
        style={{
          flex: isMobile ? undefined : 0.6,
          width: isMobile ? "100%" : undefined,
          backgroundColor: colors.gray[100],
          padding: isMobile ? spacing.lg : spacing.xxl,
          alignItems: "center",
          justifyContent: "center",
          boxShadow: isMobile
            ? undefined
            : "inset -1px 0 0 0 rgba(0, 0, 0, 0.05), inset -2px 0 0 0 rgba(0, 0, 0, 0.03)",
        }}
      >
        {/* Logo on mobile */}
        {isMobile && (
          <View style={{ marginBottom: spacing.xl, alignSelf: "center" }}>
            <img
              src="/transparent-logo.png"
              alt="Money Personality Logo"
              style={{
                height: mobileLogoSize,
                width: "auto",
                cursor: "pointer",
                objectFit: "contain",
                padding: 24,
              }}
              onClick={() => navigate("/")}
            />
          </View>
        )}
        {/* Form Content */}
        <View
          style={{
            width: "100%",
            maxWidth: isMobile ? 340 : 600,
          }}
        >
          <CustomText
            style={{
              fontSize: isMobile ? 30 : 60,
              fontWeight: "400",
              marginBottom: isMobile ? spacing.xs : spacing.sm,
              fontFamily: "Nunito",
              textAlign: isMobile ? "center" : undefined,
            }}
          >
            Create your profile
          </CustomText>
          <CustomText
            style={{
              fontSize: isMobile ? 18 : 24,
              color: colors.gray[600],
              marginBottom: isMobile ? spacing.md : spacing.lg,
              fontFamily: "Nunito",
              textAlign: isMobile ? "center" : undefined,
            }}
          >
            Please create your profile below.
          </CustomText>

          {/* Payment Confirmation Banner */}
          {/* {paymentConfirmed && (
            <View
              style={{
                backgroundColor: colors.success + "20",
                border: `1px solid ${colors.success}`,
                borderRadius: 8,
                padding: isMobile ? spacing.sm : spacing.md,
                marginBottom: isMobile ? spacing.md : spacing.lg,
              }}
            >
              <CustomText
                style={{
                  fontSize: isMobile ? 14 : 18,
                  color: colors.success,
                  fontWeight: "600",
                  fontFamily: "Nunito",
                  marginBottom: isMobile ? spacing.xs : spacing.sm,
                }}
              >
                ✅ Payment Confirmed
              </CustomText>
              <CustomText
                style={{
                  fontSize: isMobile ? 12 : 16,
                  color: colors.gray[700],
                  fontFamily: "Nunito",
                }}
              >
                Your subscription is active. Create your account to access your
                full personality profile.
              </CustomText>
            </View>
          )} */}

          {/* Account Information Section */}
          <View style={{ marginBottom: spacing.xl }}>
            <View style={{ marginBottom: spacing.md }}>
              <CustomText
                style={{
                  marginBottom: spacing.sm,
                  color: colors.dark,
                  fontFamily: "Nunito",
                  fontSize: 12,
                }}
              >
                Email
              </CustomText>
              <TextInput
                ref={emailInputRef}
                value={email}
                placeholder="john@example.com"
                placeholderTextColor={colors.gray[500]}
                style={{
                  height: 40,
                  borderWidth: 1,
                  borderColor: colors.gray[200],
                  borderRadius: 6,
                  paddingHorizontal: 12,
                  outlineStyle: "none",
                  width: "100%",
                  fontFamily: "Nunito",
                  fontSize: 14,
                  backgroundColor: colors.white,
                }}
              />
            </View>

            <View style={{ marginBottom: spacing.md }}>
              <CustomText
                style={{
                  marginBottom: spacing.sm,
                  color: colors.dark,
                  fontFamily: "Nunito",
                  fontSize: 12,
                }}
              >
                Password
              </CustomText>
              <View style={{
                position: "relative",
                width: "100%",
              }}>
                <TextInput
                  value={password}
                  onChangeText={setPassword}
                  placeholder="********"
                  placeholderTextColor={colors.gray[500]}
                  secureTextEntry={!showPassword}
                  onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (
                      e.key === "Enter" &&
                      !(!email || !password || isSigningUp)
                    ) {
                      handleSignUp();
                    }
                  }}
                  style={{
                    height: 40,
                    borderWidth: 1,
                    borderColor: colors.gray[200],
                    borderRadius: 6,
                    paddingHorizontal: 12,
                    paddingRight: 40,
                    outlineStyle: "none",
                    width: "100%",
                    fontFamily: "Nunito",
                    fontSize: 14,
                    backgroundColor: colors.white,
                  }}
                />
                <Pressable
                  onPress={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: 7,
                    padding: 4,
                  }}
                >
                  <SvgEye
                    width={20}
                    height={20}
                    color={colors.gray[500]}
                    isOpen={showPassword}
                  />
                </Pressable>
              </View>
            </View>
          </View>

          {accountExists && (
            <View style={{ marginTop: spacing.sm, marginBottom: 10, paddingLeft: 4 }}>
              <CustomText
                style={{
                  color: colors.danger,
                  fontSize: isMobile ? 14 : 18,
                  fontFamily: "Nunito",
                  marginBottom: spacing.xs,
                }}
              >
                You already have an account with this email.
              </CustomText>
              <Pressable onPress={() => navigate("/login")}>
                <CustomText
                  style={{
                    color: colors.primary,
                    fontSize: isMobile ? 14 : 18,
                    fontFamily: "Nunito",
                    textDecorationLine: "underline",
                  }}
                >
                  Log in instead
                </CustomText>
              </Pressable>
            </View>
          )}

          {error && (
            <CustomText
              style={{
                color: colors.danger,
                marginTop: spacing.sm,
                fontSize: isMobile ? 14 : 18,
                fontFamily: "Nunito",
                marginBottom: 10,
                paddingLeft: 4,
              }}
            >
              {handleApiError(error)}
            </CustomText>
          )}

          <View
            style={{
              marginBottom: spacing.xl,
            }}
          >
            <Button
              onPress={handleSignUp}
              label={
                paymentConfirmed ? "Create Account & Access Results" : "Log In"
              }
              loading={isSigningUp}
              disabled={!email || !password || isSigningUp}
              rounded={false}
              style={{
                width: "100%",
                height: 40,
              }}
              textStyle={{
                fontSize: 14,
                fontFamily: "Nunito",
              }}
            />
          </View>
        </View>
      </View>

      {/* Right Side - Testimonial */}
      <View
        style={{
          flex: isMobile ? undefined : 0.4,
          width: isMobile ? "100%" : undefined,
          backgroundColor: colors.white,
          padding: isMobile ? spacing.lg : spacing.xxl,
          alignItems: isMobile ? "center" : undefined,
          height: isMobile ? "100%" : undefined,
        }}
      >
        {/* Logo only on desktop */}
        {!isMobile && (
          <View style={{ marginBottom: spacing.xxl, alignSelf: "flex-end" }}>
            <img
              src="/transparent-logo.png"
              alt="Money Personality Logo"
              style={{
                height: 60,
                width: "auto",
                cursor: "pointer",
                objectFit: "contain",
              }}
              onClick={() => navigate("/")}
            />
          </View>
        )}
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: isMobile ? spacing.lg : 100,
          }}
        >
          <View style={{ maxWidth: 420 }}>
            <CustomText
              style={{
                fontSize: 28,
                lineHeight: 42,
                color: colors.gray[900],
                marginBottom: spacing.xl,
                fontFamily: "Nunito",
                textAlign: isMobile ? "center" : undefined,
              }}
            >
              "I can't believe how accurate the Money Personality test is...it
              felt like someone had been watching my relationship with money my
              entire life.""
            </CustomText>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: isMobile ? "center" : undefined,
              }}
            >
              <img
                src={require("../components/img/ben-pic.jpeg")}
                alt="Ben's profile"
                style={{
                  width: isMobile ? 75 : 100,
                  height: "auto",
                  borderRadius: 100,
                  objectFit: "cover",
                  marginRight: spacing.md,
                }}
              />
              <CustomText
                style={{
                  fontSize: 18,
                  color: colors.gray[600],
                  fontFamily: "Nunito",
                }}
              >
                Ben Barton, Entrepreneur
              </CustomText>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
